<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="error.errorMessage" />

        <v-form ref="form" @submit.prevent="onSubmit" color="green">
          <v-card-title class="headline">
            {{ $t('@invoiceForm/title') }}
          </v-card-title>

          <v-card-text>
            <v-text-field
              v-model="type"
              color="green darken-1"
              :label="$t('@invoiceForm/typeInputLabel')"
              disabled
              readonly
              name="type"
              :error-messages="validationError(error.errorField, errorFieldMap.type)"
            />
            <v-select
              v-model="status"
              color="green darken-1"
              :items="statuses"
              :label="$t('@invoiceForm/statusInputLabel')"
              :item-disabled="filterStatuses"
              required
              :disabled="disabledFields"
            ></v-select>
            <v-text-field
              v-model="invoice.sum"
              color="green darken-1"
              :label="$t('@invoiceForm/sumInputLabel')"
              disabled
              readonly
              type="number"
              name="sum"
              :error-messages="validationError(error.errorField, errorFieldMap.sum)"
            />
            <v-text-field
              v-model="paid"
              color="green darken-1"
              :label="$t('@invoiceForm/paidInputLabel')"
              :disabled="submitting || disablePaid || disabledFields"
              type="number"
              name="paid"
              required
              :error-messages="validationError(error.errorField, errorFieldMap.paid)"
            />
          </v-card-text>

          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn color="red darken-1" flat :disabled="submitting" @click.stop="closeModal(false)">
              {{ $t('@invoiceForm/cancelButtonLabel') }}
            </v-btn>

            <v-btn
              type="submit"
              :disabled="!isFormDirty || submitting || disabledFields"
              color="green darken-1"
              flat
            >
              {{ $t('@invoiceForm/submitButtonLabel') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { validationError, getErrorObject } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';

const stateTransition = {
  new: ['new', 'approved', 'declined'],
  pending: ['pending', 'approved', 'declined'],
  approved: ['approved'],
  processed: ['processed'],
  declined: ['declined'],
};

const errorFieldMap = {
  type: 'type',
  sum: 'sum',
  paid: 'paid',
};

export default {
  components: { ErrorsList },
  props: {
    invoice: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    const { type, status, sum = 0, paid = 0 } = this.invoice;
    return {
      submitting: false,
      error: {
        errorMessage: '',
        errorField: '',
      },
      isFormDirty: false,
      statuses: [
        { text: this.$t('@invoiceForm/newStatusLabel'), value: 'new' },
        { text: this.$t('@invoiceForm/pendingStatusLabel'), value: 'pending' },
        { text: this.$t('@invoiceForm/approvedStatusLabel'), value: 'approved' },
        { text: this.$t('@invoiceForm/declinedStatusLabel'), value: 'declined' },
        { text: this.$t('@invoiceForm/processedStatusLabel'), value: 'processed' },
      ],
      type,
      status,
      sum,
      paid,
      validationError,
      errorFieldMap,
    };
  },

  computed: {
    disabledFields() {
      return (stateTransition[this.invoice.status] || []).length <= 1;
    },

    disablePaid() {
      if (stateTransition[this.status] === stateTransition.approved) {
        return false;
      }
      return true;
    },
  },

  watch: {
    status() {
      this.onChange();
      this.paid = 0;
    },

    paid() {
      this.onChange();
    },
  },

  methods: {
    closeModal(isSent = false) {
      this.$emit('close-modal', isSent);
    },

    filterStatuses({ value }) {
      return !(stateTransition[this.invoice.status] || []).find((v) => v === value);
    },

    onChange() {
      if (!this.isFormDirty) {
        this.isFormDirty = true;
      }
      if (this.error.errorMessage) {
        this.error = {
          errorMessage: '',
          errorField: '',
        };
      }
    },

    async onSubmit() {
      const { status, paid } = this;
      const paidNumber = +paid;

      try {
        if (!status) {
          this.error = {
            errorMessage: 'Field "Status" is required',
            errorField: errorFieldMap.status,
          };
          return;
        }
        if (isNaN(paidNumber) || paidNumber < 0) {
          this.error = {
            errorMessage: 'Field "Paid" is not valid',
            errorField: errorFieldMap.paid,
          };
          return;
        }

        this.submitting = true;
        await this.$http.put(`/api/invoices/${this.invoice.id}`, { status, paid: paidNumber });
        this.closeModal(true);
      } catch (e) {
        this.error = getErrorObject(e);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
